import { useCallback, useEffect, useRef, useState } from 'react';
import type { TaskAttachment } from '../../types/crm';
import { usePostgresChangesRealtime } from '../../hooks/usePostgresChangesRealtime';
import {
  deleteTaskAttachment,
  formatTaskAttachmentBytes,
  getTaskAttachmentSignedUrl,
  listTaskAttachments,
  uploadTaskAttachment,
} from '../../lib/taskAttachments';
import { getActivePortalKind, getCrmDbSchema } from '../../lib/supabase';
import { Modal } from '../ui/Modal';

type TaskAttachmentsFieldProps = {
  /** When set, loads/saves attachments on the server. Null = new task (pending only). */
  taskId: string | null;
  canEdit: boolean;
  uploadedBy?: string;
  pendingFiles: File[];
  onPendingFilesChange: (files: File[]) => void;
  /** Compact layout for detail modal. */
  compact?: boolean;
};

type PreviewState = {
  fileName: string;
  contentType: string;
  url: string | null;
  loading: boolean;
  error: string | null;
};

function isImageType(contentType: string, fileName: string): boolean {
  if (contentType.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
}

function isPdfType(contentType: string, fileName: string): boolean {
  if (contentType === 'application/pdf' || contentType === 'application/x-pdf') return true;
  return /\.pdf$/i.test(fileName);
}

export function TaskAttachmentsField({
  taskId,
  canEdit,
  uploadedBy,
  pendingFiles,
  onPendingFilesChange,
  compact = false,
}: TaskAttachmentsFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const reload = useCallback(async () => {
    if (!taskId) return;
    setAttachments(await listTaskAttachments(taskId));
  }, [taskId]);

  useEffect(() => {
    let cancelled = false;
    if (!taskId) {
      setAttachments([]);
      return;
    }
    setError(null);
    void listTaskAttachments(taskId)
      .then((rows) => {
        if (!cancelled) setAttachments(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load attachments.');
      });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const portalKind = getActivePortalKind();
  const schema = getCrmDbSchema(portalKind === 'it' ? 'it' : 'crm');
  usePostgresChangesRealtime({
    enabled: Boolean(taskId),
    channelName: `task-attachments-live:${taskId ?? 'none'}`,
    portalKind: portalKind === 'it' ? 'it' : 'crm',
    debounceMs: 400,
    onChange: () => {
      void reload().catch(() => {
        /* ignore background refresh failures */
      });
    },
    listens: taskId
      ? [{ schema, table: 'task_attachments', filter: `task_id=eq.${taskId}` }]
      : [],
  });

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);

    if (!taskId) {
      onPendingFilesChange([...pendingFiles, ...Array.from(files)]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        await uploadTaskAttachment({ taskId, file, uploadedBy });
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openAttachment = (att: TaskAttachment) => {
    setError(null);
    setPreview({
      fileName: att.file_name,
      contentType: att.content_type || '',
      url: null,
      loading: true,
      error: null,
    });
    void getTaskAttachmentSignedUrl(att.storage_path)
      .then((url) => {
        setPreview((prev) =>
          prev && prev.fileName === att.file_name
            ? { ...prev, url, loading: false, error: null }
            : prev,
        );
      })
      .catch((e) => {
        const message = e instanceof Error ? e.message : 'Could not open file.';
        setPreview((prev) =>
          prev && prev.fileName === att.file_name
            ? { ...prev, loading: false, error: message }
            : prev,
        );
      });
  };

  const openPendingFile = (file: File) => {
    const objectUrl = URL.createObjectURL(file);
    setPreview({
      fileName: file.name,
      contentType: file.type || '',
      url: objectUrl,
      loading: false,
      error: null,
    });
  };

  const closePreview = () => {
    setPreview((prev) => {
      if (prev?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  };

  const removeSaved = async (att: TaskAttachment) => {
    if (!confirm(`Remove “${att.file_name}”?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTaskAttachment(att);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove file.');
    } finally {
      setBusy(false);
    }
  };

  const removePending = (index: number) => {
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const empty = attachments.length === 0 && pendingFiles.length === 0;
  const previewIsImage = preview
    ? isImageType(preview.contentType, preview.fileName)
    : false;
  const previewIsPdf = preview ? isPdfType(preview.contentType, preview.fileName) : false;

  return (
    <div className={`task-attach${compact ? ' task-attach--compact' : ''}`}>
      <div className="task-attach__head">
        <h4 className="task-attach__title">Attachments</h4>
        {canEdit ? (
          <>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Attach file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="task-attach__input"
              onChange={(e) => void onPickFiles(e.target.files)}
            />
          </>
        ) : null}
      </div>
      <p className="task-attach__hint">Any document type. Tap a file name to preview.</p>
      {error ? <div className="alert alert-error task-attach__error">{error}</div> : null}
      {empty ? (
        <p className="task-attach__empty">No files attached.</p>
      ) : (
        <ul className="task-attach__list">
          {attachments.map((att) => (
            <li key={att.id} className="task-attach__item">
              <button
                type="button"
                className="task-attach__link"
                onClick={() => openAttachment(att)}
                title="Preview attachment"
              >
                {att.file_name}
              </button>
              <span className="task-attach__meta">{formatTaskAttachmentBytes(att.byte_size)}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="task-attach__remove"
                  disabled={busy}
                  onClick={() => void removeSaved(att)}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
          {pendingFiles.map((file, index) => (
            <li key={`pending-${file.name}-${file.size}-${index}`} className="task-attach__item">
              <button
                type="button"
                className="task-attach__link"
                onClick={() => openPendingFile(file)}
                title="Preview attachment"
              >
                {file.name}
              </button>
              <span className="task-attach__meta">{formatTaskAttachmentBytes(file.size)}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="task-attach__remove"
                  disabled={busy}
                  onClick={() => removePending(index)}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Modal
        title={preview?.fileName ?? 'Attachment'}
        open={Boolean(preview)}
        onClose={closePreview}
        nested
        wide
        compactHeader
        panelClassName="task-attach-preview-panel"
      >
        <div className="task-attach-preview">
          {preview?.loading ? (
            <div className="empty-state">
              <div className="empty-state__spinner" />
              <p>Loading file…</p>
            </div>
          ) : preview?.error ? (
            <p className="alert alert-error">{preview.error}</p>
          ) : preview?.url ? (
            <>
              {previewIsImage ? (
                <img
                  className="task-attach-preview__image"
                  src={preview.url}
                  alt={preview.fileName}
                />
              ) : previewIsPdf ? (
                <iframe
                  className="task-attach-preview__frame"
                  src={preview.url}
                  title={preview.fileName}
                />
              ) : (
                <div className="task-attach-preview__fallback">
                  <p>Preview is not available for this file type.</p>
                  <a
                    className="btn-primary btn-sm"
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={preview.fileName}
                  >
                    Open / Download
                  </a>
                </div>
              )}
              {(previewIsImage || previewIsPdf) && (
                <div className="task-attach-preview__actions">
                  <a
                    className="btn-secondary btn-sm"
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={preview.fileName}
                  >
                    Open in new tab
                  </a>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
